import {
  OrderItem,
  Product,
  ProductPackageTypeEnum,
  ProductVariant,
  SubscriptionItem,
} from '#apollo/schema';

export type ProductTid = {
  id: string;
  tid: string;
  displayTheme: string;
};

export type ProductVariantTid = {
  id: string;
  tid: string;
};

export enum SubscriptionStatusEnum {
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  CANCELLED = 'CANCELLED',
}

export enum CustomerTypeEnum {
  membership = 'membership',
  subscription = 'subscription',
}

// For a given set of tally product ids return the associated shopify PRODUCT ids
export const getProductIdsByTerm = (products: Product[], terms: Readonly<string[]>): string[] => {
  return products?.reduce((acc: string[], product) => {
    if (terms.includes(product.tid)) {
      acc.push(product.id.toString());
    }
    return acc;
  }, []);
};

// For a given tally product variant term return the associated shopify PRODUCT VARIANT id
export const getProductVariantIdByVariantTerm = (
  productVariants: ProductVariantTid[],
  productVariantTerm: string,
): string | null => {
  const variant = productVariants.find(
    (variant: ProductVariantTid) => variant.tid === productVariantTerm,
  );

  return variant ? variant.id.toString() : null;
};

// For a given set of tally product ids return the associated shopify PRODUCT VARIANT ids
export const getProductVariantIdsByTerm = (
  productVariants: ProductVariant[],
  productTerms: Readonly<string[]>,
): string[] => {
  return productVariants?.reduce((acc: string[], variant: ProductVariant) => {
    if (variant.product?.tid && productTerms.includes(variant.product?.tid)) {
      acc.push(variant.id.toString());
    }
    return acc;
  }, []);
};

// Check if a list of subscription items contains the provided product ids
export const checkSubscriptionContainsByProductIds = (
  items: SubscriptionItem[],
  productIds: string[],
): boolean => {
  return items.some(item => productIds.includes(item.productId.toString()));
};

// Check if a subscription contains the provided product ids
export const hasSubscriptionItem = (
  subscriptionItems: SubscriptionItem[],
  productIds: string[],
): boolean =>
  subscriptionItems && !!subscriptionItems.length && !!productIds.length
    ? checkSubscriptionContainsByProductIds(subscriptionItems, productIds)
    : false;

// Check that the given list contains an object associated with a given tally id
export const checkItHasByTerm = (
  items: SubscriptionItem[] | OrderItem[] | string[] = [],
  productVariants: ProductVariant[],
  term: string,
): boolean => {
  return items.some(item => {
    let idToCheck: string;
    if (typeof item === 'string') {
      idToCheck = item;
    } else {
      idToCheck = 'variantId' in item ? item.variantId : item.id;
    }
    const itemVariant = productVariants?.find(variant => variant?.id === idToCheck);
    if (!itemVariant) {
      return false;
    }
    return itemVariant.tid === term;
  });
};

// Given a list of product ids, return a list of product Tids
export const getProductTidsByProductIds = (
  productVariants: ProductVariant[],
  productIds: string[],
): string[] => {
  return productVariants.reduce((acc: string[], variant: ProductVariant) => {
    if (variant.product?.id && productIds.includes(variant.product?.id)) {
      acc.push(variant.product.tid);
    }
    return acc;
  }, []);
};

// Given a list of product Tids, return a list of product Ids
export const getProductIdsByProductTids = (
  productVariants: ProductVariant[],
  productTids: string[],
): string[] => {
  return Array.from(
    new Set(
      productVariants.reduce((acc: string[], variant: ProductVariant) => {
        if (variant.product?.tid && productTids.includes(variant.product.tid)) {
          acc.push(variant.product.id);
        }
        return acc;
      }, []),
    ),
  );
};

// Given a list of variant ids
// Return a list of corresponding product ids
export const getProductIdsByVariantIds = (
  productVariants: ProductVariant[],
  variantIds: string[],
): string[] => {
  return productVariants.reduce((acc: string[], variant: ProductVariant) => {
    if (variant.product?.id && variantIds.includes(variant.id)) {
      acc.push(variant.product.id);
    }
    return acc;
  }, []);
};

// Given a list of product ids
// Return a list of corresponding variant ids
export const getVariantIdsByProductIds = (
  productVariants: ProductVariant[],
  productIds: string[],
): string[] => {
  return productVariants.reduce((acc: string[], variant: ProductVariant) => {
    if (variant.product?.id && productIds.includes(variant.product.id)) {
      acc.push(variant.id);
    }
    return acc;
  }, []);
};

// Given a list of supplement variant ids containing bottle and pouch variants
// Return a list of each corresponding bottle variant ids
export const getBottleVariantIdsByVariantIds = (
  productVariants: ProductVariant[],
  variantIds: string[],
): string[] => {
  const bottleVariantIds: string[] = [];

  variantIds.forEach(vId => {
    const variantFromGivenId = productVariants.find(variant => variant.id === vId);
    if (variantFromGivenId?.packageType?.toLowerCase() === ProductPackageTypeEnum.Bottle) {
      bottleVariantIds.push(vId);
    } else {
      const bottleVariant = productVariants.find(
        variant =>
          variant.packageType?.toLowerCase() == ProductPackageTypeEnum.Bottle &&
          variant.productId == variantFromGivenId?.productId,
      );
      if (bottleVariant) {
        bottleVariantIds.push(bottleVariant.id);
      }
    }
  });

  return bottleVariantIds;
};

// Given a list of variant ids that could contain bottle and pouch variants
// Check if the variant is in the subscription and return the variant of the item in the subscription
// If the variant is not in the subscription, return the variant of another item in the subscription
// with the same productId
export const getSubscriptionVariantIdsByVariantIds = (
  subscriptionItems: SubscriptionItem[],
  productVariants: ProductVariant[],
  variantIds: string[],
): string[] => {
  const subscriptionVariantIds: string[] = subscriptionItems.map(item => item.variantId);

  const convertedSubscriptionVariantIds: string[] = [];
  variantIds.forEach(vId => {
    if (subscriptionVariantIds.includes(vId)) {
      convertedSubscriptionVariantIds.push(vId);
    } else {
      const variantObject = productVariants.find(pv => pv.id == vId);
      const matchingSubscriptionVariant = productVariants.find(pv => {
        return pv.id != vId && pv.productId == variantObject?.productId;
      });
      if (matchingSubscriptionVariant) {
        convertedSubscriptionVariantIds.push(matchingSubscriptionVariant.id);
      }
    }
  });
  return convertedSubscriptionVariantIds;
};

// Returns a list of variant ids in a subscription
// Given a list of product ids
export const getSubscriptionVariantIdsByProductIds = (
  subscriptionItems: SubscriptionItem[],
  productIds: string[],
): string[] => {
  const variantIds: string[] = [];
  const subscriptionProductIdVariantIdMap: Map<string, string> = new Map();

  subscriptionItems.forEach(item => {
    subscriptionProductIdVariantIdMap.set(item.productId, item.variantId);
  });

  productIds.forEach(productId => {
    const variantId = subscriptionProductIdVariantIdMap.get(productId);
    if (variantId) {
      variantIds.push(variantId);
    }
  });
  return variantIds;
};
